// ============================================================================
// API Client Factory
// Extracted from AppointmentForm.js — pure async functions, no component state.
//
// Usage in AppointmentForm.js:
//   const api = useMemo(
//     () => createApiClient(API_BASE_URL, addToDebugLog, setLastApiResponse, setApiError),
//     // eslint-disable-next-line react-hooks/exhaustive-deps
//     [API_BASE_URL]
//   );
// ============================================================================

import { DEBUG_MODE } from './constants';

/**
 * Creates a bound API client whose methods share a single apiCall core.
 *
 * @param {string}   apiBaseUrl        - FastAPI base URL (from constants.API_BASE_URL)
 * @param {Function} addToDebugLog     - (action, data, response, error) => void
 * @param {Function} setLastApiResponse - React state setter
 * @param {Function} setApiError       - React state setter
 */
export function createApiClient(apiBaseUrl, addToDebugLog, setLastApiResponse, setApiError) {

  // Core fetch wrapper
  async function apiCall(endpoint, method = 'GET', body = null, description = '', contentType = 'application/json') {
    const baseUrl = apiBaseUrl.endsWith('/') ? apiBaseUrl.slice(0, -1) : apiBaseUrl;
    const cleanEndpoint = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
    const url = `${baseUrl}${cleanEndpoint}`;

    const config = {
      method,
      headers: {
        'Content-Type': contentType,
        'Accept': 'application/json'
      }
    };

    if (body && method !== 'GET') {
      if (contentType === 'application/x-www-form-urlencoded; charset=UTF-8') {
        const formData = new URLSearchParams();
        Object.entries(body).forEach(([key, value]) => {
          formData.append(key, value);
        });
        config.body = formData.toString();
      } else {
        config.body = JSON.stringify(body);
      }
    }

    addToDebugLog(`API Call: ${method} ${endpoint}`, { url, body }, null, null);
    if (DEBUG_MODE) {
      console.log(`🔍 [API] Base URL: ${apiBaseUrl}`);
      console.log(`🔍 [API] Clean Base URL: ${baseUrl}`);
      console.log(`🔍 [API] Clean Endpoint: ${cleanEndpoint}`);
      console.log(`🔍 [API] Final URL: ${url}`);
      console.log(`🔍 [API] Making ${method} request to: ${url}`);
      console.log(`🔍 [API] Request body:`, body);
      console.log(`🔍 [API] Content-Type:`, contentType);
    }

    try {
      const response = await fetch(url, config);
      if (DEBUG_MODE) {
        console.log(`🔍 [API] Response status: ${response.status} ${response.statusText}`);
      }
      const data = await response.json();

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${data.detail || response.statusText}`);
      }

      addToDebugLog(`API Success: ${description || endpoint}`, { url, body }, data, null);
      setLastApiResponse(data);
      setApiError('');
      return data;
    } catch (error) {
      addToDebugLog(`API Error: ${description || endpoint}`, { url, body }, null, error.message);
      setApiError(`${description || endpoint} failed: ${error.message}`);
      throw error;
    }
  }

  // ---------------------------------------------------------------------------
  // Endpoint wrappers
  // ---------------------------------------------------------------------------

  function lookupZipcode(zipcode) {
    return apiCall('/patient/zipcode', 'POST', { prefix: zipcode }, 'ZIP Code Lookup');
  }

  function saveNewPatient(patientData) {
    return apiCall('/patient/save-new-patient', 'POST', patientData, 'Save New Patient');
  }

  function searchExistingPatient(searchData) {
    return apiCall('/existing-patient/search', 'POST', searchData, 'Search Existing Patient');
  }

  function getProviders(filterData) {
    return apiCall('/provider/providers', 'POST', filterData, 'Get Providers');
  }

  async function getTimeSlots(scheduleData) {
    if (DEBUG_MODE) {
      console.log('🔍 [API REQUEST] Getting time slots from backend:', {
        endpoint: '/provider/schedule-timeslot',
        requestData: scheduleData
      });
    }

    const response = await apiCall('/provider/schedule-timeslot', 'POST', scheduleData, 'Get Time Slots');

    if (DEBUG_MODE && response && response.schedulerSlot) {
      console.log('📥 [API RESPONSE] Time slots received from backend:', {
        totalSlots: response.schedulerSlot.length,
        availableSlots: response.schedulerSlot.filter(s => s.IsAvailable === 1 && !s.IsDisabled).length,
        sampleData: response.schedulerSlot.slice(0, 2).map(slot => ({
          Time: slot.Time,
          ScheduleID: slot.ScheduleID,
          Duration: slot.Duration,
          IsAvailable: slot.IsAvailable,
          IsDisabled: slot.IsDisabled
        }))
      });
    }

    return response;
  }

  function bookAppointment(bookingPayload) {
    return apiCall('/booking/book', 'POST', bookingPayload, 'Book Appointment');
  }

  async function testServerConnection() {
    try {
      const response = await fetch(`${apiBaseUrl}/`, { method: 'GET' });
      if (DEBUG_MODE) {
        console.log(`🔍 [SERVER] Server response: ${response.status} ${response.statusText}`);
      }
      return response.ok;
    } catch (error) {
      if (DEBUG_MODE) {
        console.error(`🔍 [SERVER] Server connection failed:`, error);
      }
      return false;
    }
  }

  return {
    apiCall,
    lookupZipcode,
    saveNewPatient,
    searchExistingPatient,
    getProviders,
    getTimeSlots,
    bookAppointment,
    testServerConnection
  };
}
